import React, { useState, useEffect, useContext, useMemo } from "react";
import "../pages/DriverStatus.css";
import { useLocation, useSearchParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import DriverCard from "../components/driverCard/DriverCard";
import DriverDetailModal from "../components/driverDetailModal/DriverDetailModal";
import BroadcastComponent from "../components/broadcast/Broadcast";
import DriverFilter from "../components/driverFilter/DriverFilter";
import { useTranslation } from "react-i18next";
import { useGlobalToast } from "../context/ToastProvider";
import { fleetErrorMessage } from "../utils/fleetFeedback";
import ChatManager from "../components/chat/ChatManager";
import { useChat } from "../context/ChatContext";
import { DriverContext } from "../context/DriverContext";
import { MailOutlined, MessageOutlined, TeamOutlined } from "@ant-design/icons";
import { isDriverMessage } from "../utils/messageRole";
import ContextToolbar from "../components/contextToolbar/ContextToolbar";
import { MdLocalShipping, MdOutlineLocalShipping, MdOutlinePersonOff } from "react-icons/md";
import { sendFleetMessageToDriver } from "../services/fleetConversationService";
import { removeFleetDriver } from "../services/fleetDriverService";
import RegisterDriver from "../components/registerDriver/RegisterDriver";

export default function DriverStatus() {
  const [driverCard, setDriverCards] = useState([]);
  const [activeDriver, setActiveDriver] = useState(null);
  const [isDriverDetailOpen, setIsDriverDetailOpen] = useState(false);
  const { getFleetID, fleetData } = useContext(AuthContext);
  const { state } = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tags, setTags] = useState([]);
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeLayers, setActiveLayers] = useState(["availableWithOrder", "availableWithoutOrder"]);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [unreadByDriver, setUnreadByDriver] = useState({});
  const { openChat, openChats } = useChat();
  const { driversDetails } = useContext(DriverContext);
  const fleetId = getFleetID();

  const { t } = useTranslation();
  const toast = useGlobalToast();
  const blockingStatuses = new Set(["ASSIGNED", "ACTIVE"]);
  const normalizeFleetId = (value) => String(value || "").trim();
  const normalizeId = (value) => String(value || "").trim();
  const getDriverLayer = (driver) => {
    const currentFleet = normalizeFleetId(driver?.currentFleet);
    const currentOrder = normalizeFleetId(driver?.currentOrder);
    const isAvailable = currentFleet === normalizeFleetId(fleetId);
    if (!isAvailable) return "notAvailable";
    return currentOrder ? "availableWithOrder" : "availableWithoutOrder";
  };

  const markDriverConversationAsRead = async (fleetIdToUse, driverId) => {
    const driverConversationQuery = query(
      collection(db, "fleets", fleetIdToUse, "conversations"),
      where("userId", "==", String(driverId))
    );
    const driverConversationSnapshot = await getDocs(driverConversationQuery);
    if (driverConversationSnapshot.empty) return;

    let batch = writeBatch(db);
    let pendingOperations = 0;

    for (const messageDoc of driverConversationSnapshot.docs) {
      const messageData = messageDoc.data() || {};
      if (!isDriverMessage(messageData)) continue;
      if (messageData.read === true) continue;

      batch.update(messageDoc.ref, { read: true });
      pendingOperations += 1;

      if (pendingOperations >= 450) {
        await batch.commit();
        batch = writeBatch(db);
        pendingOperations = 0;
      }
    }

    if (pendingOperations > 0) {
      await batch.commit();
    }
  };

  const publishDriverRemovalMessage = async (fleetIdToUse, driverId, fleetName) => {
    const fleetLabel = fleetName ? `fleet ${fleetName}` : "this fleet";
    await sendFleetMessageToDriver({
      fleetId: fleetIdToUse,
      driverId: String(driverId),
      message: `You have been removed from ${fleetLabel}.`,
      orderId: null,
    });
  };

  const deleteDriver = async (driverId, options = {}) => {
    const { dryRun = false, silent = false } = options;

    try {
      if (!fleetId || !driverId) {
        const reason = t("driverDetail.removeMissingContext", {
          defaultValue: "Missing fleet or driver information to remove this driver.",
        });
        return { ok: false, reason };
      }

      const normalizedFleetId = normalizeFleetId(fleetId);
      const transportOrdersRef = collection(db, "fleets", normalizedFleetId, "transport_orders");
      const driverOrdersQuery = query(transportOrdersRef, where("userId", "==", String(driverId)));
      const driverOrdersSnapshot = await getDocs(driverOrdersQuery);

      const blockingOrders = [];
      driverOrdersSnapshot.forEach((orderDoc) => {
        const order = orderDoc.data() || {};
        const status = String(order.status || "").toUpperCase();
        if (!blockingStatuses.has(status)) return;
        blockingOrders.push({
          id: orderDoc.id,
          ref: order.internalReference || order.customerReference || order.transportOrderRef || orderDoc.id,
          status,
        });
      });

      if (blockingOrders.length > 0) {
        const listedOrders = blockingOrders
          .slice(0, 3)
          .map((order) => `${order.ref} (${order.status})`)
          .join(", ");
        const remaining = blockingOrders.length > 3 ? ` +${blockingOrders.length - 3}` : "";
        const reason = t("driverDetail.removeBlockedByOrders", {
          defaultValue:
            "Removal is not possible because this driver still has assigned or active orders in this fleet: {{orders}}",
          orders: `${listedOrders}${remaining}`,
        });
        return { ok: false, reason };
      }

      if (dryRun) {
        return { ok: true };
      }

      let fleetName = String(fleetData?.name || "").trim();
      if (!fleetName) {
        const fleetDoc = await getDoc(doc(db, "fleets", normalizedFleetId));
        fleetName = String(fleetDoc.data()?.name || "").trim();
      }

      await removeFleetDriver(normalizedFleetId, driverId);
      // Removal is authoritative; notification failures must not report it as failed.
      try {
        await markDriverConversationAsRead(normalizedFleetId, driverId);
        await publishDriverRemovalMessage(normalizedFleetId, driverId, fleetName);
      } catch (error) {
        console.warn("Driver removed, but conversation notification failed:", error);
      }
      setDriverCards((prevCards) => prevCards.filter((driver) => driver.id !== driverId));
      setUnreadByDriver((prevMap) => {
        const nextMap = { ...prevMap };
        delete nextMap[String(driverId)];
        return nextMap;
      });
      if (!silent) {
        toast({
          title: t("toast.successDeleteDriver"),
          status: "success",
        });
      }
      return { ok: true };
    } catch (error) {
      console.error("Error deleting driver:", error);
      const reason = fleetErrorMessage(t, error, t("toast.errorDeleteDriver"));
      if (!silent) {
        toast({ error,
          title: reason,
          status: "error",
        });
      }
      return { ok: false, reason };
    }
  };

  useEffect(() => {
    setDriverCards(driversDetails);
    setTags(driversDetails.flatMap((driver) => driver.tags || []));
  }, [driversDetails]);

  useEffect(() => {
    if (!fleetId) {
      setUnreadByDriver({});
      return undefined;
    }

    const unreadQuery = query(
      collection(db, "fleets", fleetId, "conversations"),
      where("read", "==", false)
    );

    const unsubscribe = onSnapshot(
      unreadQuery,
      (snapshot) => {
        const unreadMap = {};

        snapshot.forEach((docSnap) => {
          const data = docSnap.data() || {};
          if (!isDriverMessage(data)) return;
          const driverId = data.userId;
          if (!driverId) return;
          unreadMap[driverId] = (unreadMap[driverId] || 0) + 1;
        });

        setUnreadByDriver(unreadMap);
      },
      (error) => {
        console.error("Error subscribing unread driver messages:", error);
      }
    );

    return () => unsubscribe();
  }, [fleetId]);

  useEffect(() => {
    if (state != null && state.driver != null) {
      setActiveDriver(state.driver);
      setIsDriverDetailOpen(true);
    }
  }, [state]);

  useEffect(() => {
    const assistantDriverId = normalizeId(searchParams.get("assistantDriverId"));
    const shouldOpenDetail = searchParams.get("assistantDriverDetail") === "1";
    const shouldOpenChat = searchParams.get("assistantDriverChat") === "1";
    if (!assistantDriverId || (!shouldOpenDetail && !shouldOpenChat)) return;

    const driver =
      driverCard.find((entry) => normalizeId(entry?.id) === assistantDriverId) ||
      driversDetails.find((entry) => normalizeId(entry?.id) === assistantDriverId) ||
      null;

    if (driver) {
      if (shouldOpenDetail) {
        setActiveDriver(driver);
        setIsDriverDetailOpen(true);
      }
      if (shouldOpenChat) {
        openChat({
          ...driver,
          id: driver.id,
          name: driver.name,
          driverStatus: driver.isOnline ? "online" : "offline",
          profilePic: driver.profilePic,
          type: "All",
        });
      }
    }

    const next = new URLSearchParams(searchParams);
    next.delete("assistantDriverId");
    next.delete("assistantDriverDetail");
    next.delete("assistantDriverChat");
    setSearchParams(next, { replace: true });
  }, [driverCard, driversDetails, openChat, searchParams, setSearchParams]);

  useEffect(() => {
    const lowerSearch = searchTerm.toLowerCase();

    const nextFiltered = driverCard.filter((driver) => {
      const layerKey = getDriverLayer(driver);
      if (!activeLayers.includes(layerKey)) return false;

      const nameMatch = driver.name?.toLowerCase().includes(lowerSearch);
      const tagMatch = driver.tags?.some((tag) => tag.toLowerCase().includes(lowerSearch));
      const searchMatch = !lowerSearch || nameMatch || tagMatch;
      if (!searchMatch) return false;

      if (!showUnreadOnly) return true;
      return (unreadByDriver[driver.id] || 0) > 0;
    });

    setFilteredDrivers(nextFiltered);
  }, [activeLayers, driverCard, searchTerm, unreadByDriver, showUnreadOnly]);

  const handleFilterChange = (searchText) => {
    setSearchTerm((searchText || "").toLowerCase());
  };

  const unreadDriversCount = Object.values(unreadByDriver).filter((count) => Number(count) > 0).length;
  const layerCounts = driverCard.reduce(
    (acc, driver) => {
      const layer = getDriverLayer(driver);
      acc[layer] = (acc[layer] || 0) + 1;
      return acc;
    },
    { notAvailable: 0, availableWithOrder: 0, availableWithoutOrder: 0 }
  );

  const toggleLayer = (layerKey) => {
    setActiveLayers((prevLayers) =>
      prevLayers.includes(layerKey)
        ? prevLayers.filter((activeKey) => activeKey !== layerKey)
        : [...prevLayers, layerKey]
    );
  };

  const toolbarCategories = useMemo(
    () => [
      {
        key: "drivers",
        triggerLabel: t("contextToolbar.categories.drivers", { defaultValue: "Driver layers" }),
        triggerIcon: <TeamOutlined />,
        options: [
          {
            key: "availableWithOrder",
            icon: <MdLocalShipping size={16} />,
            active: activeLayers.includes("availableWithOrder"),
            count: layerCounts.availableWithOrder,
            tooltip: t("contextToolbar.options.availableWithOrder", {
              defaultValue: "Available drivers with active order",
            }),
            onToggle: () => toggleLayer("availableWithOrder"),
          },
          {
            key: "availableWithoutOrder",
            icon: <MdOutlineLocalShipping size={16} />,
            active: activeLayers.includes("availableWithoutOrder"),
            count: layerCounts.availableWithoutOrder,
            tooltip: t("contextToolbar.options.availableWithoutOrder", {
              defaultValue: "Available drivers without active order",
            }),
            onToggle: () => toggleLayer("availableWithoutOrder"),
          },
          {
            key: "notAvailable",
            icon: <MdOutlinePersonOff size={16} />,
            active: activeLayers.includes("notAvailable"),
            count: layerCounts.notAvailable,
            tooltip: t("contextToolbar.options.notAvailable", {
              defaultValue: "Drivers not working in this fleet",
            }),
            onToggle: () => toggleLayer("notAvailable"),
          },
        ],
      },
      {
        key: "messages",
        triggerLabel: t("contextToolbar.categories.messages", {
          defaultValue: "Message filters",
        }),
        triggerIcon: <MessageOutlined />,
        options: [
          {
            key: "pendingMessages",
            icon: <MailOutlined />,
            active: showUnreadOnly,
            count: unreadDriversCount,
            tooltip: t("contextToolbar.options.pendingMessages", {
              defaultValue: "Show only drivers with pending messages",
            }),
            onToggle: () => setShowUnreadOnly((prev) => !prev),
          },
        ],
      },
    ],
    [activeLayers, layerCounts, showUnreadOnly, t, unreadDriversCount]
  );

  return (
    <div className={`driverPage ${isDriverDetailOpen ? "driverPage--detail-open" : ""}`}>
      <div className="driver-toolbar-wrapper">
        <ContextToolbar
          searchSlot={
            <DriverFilter
              tags={tags}
              names={driverCard.map((d) => d.name)}
              onFilter={handleFilterChange}
            />
          }
          categories={toolbarCategories}
          actionsSlot={<div style={{display:"flex",gap:8,alignItems:"center"}}><RegisterDriver /><BroadcastComponent tags={tags} drivers={driverCard} compact /></div>}
        />
      </div>

      <div className="chatAndCard-section">
        <div className="cards-container">
          {filteredDrivers !== null && filteredDrivers.length > 0 ? (
            filteredDrivers.map((driver) => {
              const isDriverChatOpen = openChats.some(
                (chat) => String(chat?.id || "") === String(driver.id) && chat?.isMinimized !== true
              );
              const unreadMessages = isDriverChatOpen ? 0 : unreadByDriver[driver.id] || 0;

              return (
                <div className="driver-list" key={driver.id}>
                  <DriverCard
                    profilePic={driver.profilePic}
                    name={driver.name}
                    truck_plate={driver.truck_plate}
                    location={driver.isOnline}
                    id={driver.id}
                    deleteDriver={deleteDriver}
                    driver={driver}
                    driverStatus={driver.isOnline}
                    unreadMessages={unreadMessages}
                    onOpenDetails={(driverSelected) => {
                      setActiveDriver(driverSelected);
                      setIsDriverDetailOpen(true);
                    }}
                    onChatClick={(driver) => {
                      openChat({
                        ...driver,
                        id: driver.id,
                        name: driver.name,
                        driverStatus: driver.isOnline ? "online" : "offline",
                        profilePic: driver.profilePic,
                        type: "All",
                      });
                    }}
                  />
                </div>
              );
            })
          ) : (
            <p>{t("driverFilter.noDriversFound")}</p>
          )}
        </div>
        <DriverDetailModal
          isOpen={isDriverDetailOpen && !!activeDriver}
          onClose={() => {
            setIsDriverDetailOpen(false);
            setActiveDriver(null);
          }}
          driverId={activeDriver?.id}
          onChatClick={(driver) => {
            openChat({
              ...driver,
              id: driver.id,
              name: driver.name,
              driverStatus: driver.isOnline ? "online" : "offline",
              profilePic: driver.profilePic,
              type: "All",
            });
          }}
          deleteDriver={deleteDriver}
        />
      </div>
      <ChatManager rightOffset={isDriverDetailOpen ? 500 : 20} />
    </div>
  );
}
